// AERIAL-PERSPECTIVE FOG — the "atmosphere" upgrade over stock linear THREE.Fog.
//
// Stock fog fades every opaque fragment toward ONE flat per-biome hex, ignoring view direction, sun, and
// altitude — so distant terrain smears to a color that doesn't even match the (unfogged) sky behind it.
// This replaces that with Roblox-Atmosphere-style aerial perspective, done cheaply by reusing assets we
// already have. The trick that makes it near-free: every visible battle material is a three BUILT-IN
// material (MeshStandard/Lambert/Basic) sharing three's fog shader chunks, and only the sky dome / wind /
// perimeter opt out (`fog:false`). So a single GLOBAL `THREE.ShaderChunk` override upgrades fog on ALL
// world geometry at once, with the sky auto-excluded — no per-material edits.
//
// The model (per opaque fragment):
//   • fade target = the ACTUAL per-direction sky color, sampled from the baked scattering cubemap the
//     dome already uses (`uFogSky`). Distant geometry dissolves INTO the real sky ⇒ the horizon seam
//     vanishes and directional tint + sun-side warmth come for free.
//   • extinction = analytic exponential HEIGHT fog (Íñigo-Quílez integral): density falls off with
//     altitude, so low ground is hazier than high peaks and looking up is clearer than looking across.
//   • sun glow = a Henyey–Greenstein forward lobe toward the sun (Roblox "Glare").
//
// Color space: in three r171 the fog chunk runs AFTER tonemapping + colorspace (opaque→tonemap→colorspace
// →fog), i.e. in DISPLAY space — which is exactly the space the baked cubemap is encoded in. So we sample
// `textureCube(uFogSky, dir)` and mix directly, no linearization. (The sun-glow tint is converted to sRGB
// in setFogSky to sit in the same space.)

import * as THREE from "three";

// ---- shared uniforms: ONE object, Object.assign'd (BY REFERENCE) into every fogged world material via
// patchFogMaterial — so a single write here retints the fog on terrain, units, tanks, giants, projectiles… ----
export const fogUniforms = {
  uFogAerial: { value: 0 },                              // 0 = classic linear fallback (sandbox), 1 = aerial
  uFogSky: { value: null as THREE.Texture | null },      // baked per-direction sky cubemap (the fade target)
  uFogSunDir: { value: new THREE.Vector3(0, 1, 0) },
  uFogSunColor: { value: new THREE.Color(1, 1, 1) },     // display-space, for the forward sun glow
  uFogDensity: { value: 2e-4 },                          // master optical density per stud (at ground level)
  uFogHeightRef: { value: 0 },                           // altitude (studs) where density == uFogDensity
  uFogHeightFalloff: { value: 1 / 1400 },                // density e-folding rate per stud aloft (thinner up high)
  uFogGlare: { value: 0.3 },                             // Henyey–Greenstein sun-forward glow strength
  uFogGlareG: { value: 0.74 },                           // HG asymmetry (→1 = tighter forward lobe)
  uFogMax: { value: 1 },                                 // opacity cap (1 = full dissolve; <1 = keep silhouettes)
  uFogStorm: { value: 1 },                               // transient density multiplier (dust-storm pulse)
};

// ---------------------------------------------------------------------------------------------------------
// GLSL chunk overrides. `transformed` (skinned local pos), `modelMatrix`, `instanceMatrix`, and
// `cameraPosition` are all three built-ins in scope here — do NOT redeclare cameraPosition.
// ---------------------------------------------------------------------------------------------------------
const FOG_PARS_VERTEX = /* glsl */ `
#ifdef USE_FOG
  varying vec3 vFogWorldPos;
#endif
`;

const FOG_VERTEX = /* glsl */ `
#ifdef USE_FOG
  // Reconstruct WORLD position from the view-space position. mvPosition already bakes in skinning AND
  // instancing (it's set in project_vertex, before fog_vertex), and — crucially — EVERY fogged material
  // defines it, including sprites/points/lines that never define \`transformed\`. viewMatrix is rigid, so
  // its inverse rotation is transpose(mat3(viewMatrix)) and its inverse translation is cameraPosition.
  // GLSL identity: (vec * mat) == (transpose(mat) * vec), so multiply on the right and skip the transpose().
  vFogWorldPos = mvPosition.xyz * mat3( viewMatrix ) + cameraPosition;
#endif
`;

const FOG_PARS_FRAGMENT = /* glsl */ `
#ifdef USE_FOG
  uniform vec3 fogColor;      // renderer-fed (classic fallback path)
  uniform float fogNear;
  uniform float fogFar;
  varying vec3 vFogWorldPos;
  // The aerial path (and especially its samplerCube) exists ONLY on materials patchFogMaterial has
  // visited: an UNPATCHED material would declare uFogSky with no bound texture — the default sampler
  // slot 0 then collides with a sampler2D (e.g. the shadow map) and GL DROPS the draw (invisible
  // meshes; this bit the army's late-created wave materials). Unpatched ⇒ classic fog, always safe.
  #ifdef AERIAL_FOG
  uniform float uFogAerial;
  uniform samplerCube uFogSky;
  uniform vec3 uFogSunDir;
  uniform vec3 uFogSunColor;
  uniform float uFogDensity;
  uniform float uFogHeightRef;
  uniform float uFogHeightFalloff;
  uniform float uFogGlare;
  uniform float uFogGlareG;
  uniform float uFogMax;
  uniform float uFogStorm;
  #endif
#endif
`;

const FOG_FRAGMENT = /* glsl */ `
#ifdef USE_FOG
  vec3 fogRay = vFogWorldPos - cameraPosition;
  float fogDist = length( fogRay );
  #ifndef AERIAL_FOG
  {
    // UNPATCHED material (no fog uniforms bound): classic linear fog — safe on any material
    float fFog = smoothstep( fogNear, fogFar, fogDist );
    gl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, fFog );
  }
  #else
  if ( uFogAerial < 0.5 ) {
    // classic linear fog (sandbox / no baked sky) — unchanged look
    float fFog = smoothstep( fogNear, fogFar, fogDist );
    gl_FragColor.rgb = mix( gl_FragColor.rgb, fogColor, fFog );
  } else {
    vec3 fogDir = fogRay / max( fogDist, 1e-4 );
    // analytic exponential height-fog: optical depth integrated along the view ray. base is the density at
    // the camera's altitude; the (1-exp(-dist*dir.y*k))/(dir.y*k) factor is the closed-form integral of the
    // exp(-y*k) density profile along the ray (→ base*dist for a horizontal ray).
    float kh = uFogHeightFalloff;
    float ty = fogDir.y * kh;
    float base = uFogDensity * uFogStorm * exp( -( cameraPosition.y - uFogHeightRef ) * kh );
    float optical = ( abs( ty ) > 1e-5 ) ? base * ( 1.0 - exp( -fogDist * ty ) ) / ty : base * fogDist;
    float fogFactor = ( 1.0 - exp( -max( optical, 0.0 ) ) ) * uFogMax;
    // fade toward the ACTUAL sky color in this exact view direction (same display-encoded cubemap the dome
    // samples ⇒ the horizon seam disappears; aerial tint + sun-side warmth are baked in).
    vec3 fogCol = textureCube( uFogSky, fogDir ).rgb;
    // Henyey–Greenstein forward glow: the haze brightens toward the sun.
    float mu = dot( fogDir, uFogSunDir );
    float g = uFogGlareG;
    float hg = ( 1.0 - g * g ) / pow( max( 1.0 + g * g - 2.0 * g * mu, 1e-4 ), 1.5 ) * 0.07957747;
    fogCol += uFogSunColor * ( hg * uFogGlare );
    gl_FragColor.rgb = mix( gl_FragColor.rgb, fogCol, clamp( fogFactor, 0.0, 1.0 ) );
  }
  #endif
#endif
`;

let _installed = false;
let _dummyCube: THREE.CubeTexture | null = null;

/** Override three's global fog shader chunks. Call ONCE at boot, before any material compiles. Idempotent. */
export function installAerialFog(): void {
  if (_installed) return;
  _installed = true;
  // A valid (tiny) cube keeps the samplerCube complete in the sandbox, where the aerial branch is never
  // taken but the sampler is still declared — avoids incomplete-texture warnings on strict drivers.
  if (!_dummyCube) {
    const c = document.createElement("canvas");
    c.width = c.height = 1;
    const g = c.getContext("2d");
    if (g) { g.fillStyle = "#000"; g.fillRect(0, 0, 1, 1); }
    _dummyCube = new THREE.CubeTexture([c, c, c, c, c, c]);
    _dummyCube.needsUpdate = true;
  }
  if (!fogUniforms.uFogSky.value) fogUniforms.uFogSky.value = _dummyCube;
  THREE.ShaderChunk.fog_pars_vertex = FOG_PARS_VERTEX;
  THREE.ShaderChunk.fog_vertex = FOG_VERTEX;
  THREE.ShaderChunk.fog_pars_fragment = FOG_PARS_FRAGMENT;
  THREE.ShaderChunk.fog_fragment = FOG_FRAGMENT;
}

interface Patchable {
  fog?: boolean;
  userData: Record<string, unknown>;
  defines?: Record<string, string>;
  onBeforeCompile: THREE.Material["onBeforeCompile"];
  needsUpdate: boolean;
}

/** Wrap a material's onBeforeCompile to bind the shared fog uniforms (by reference) and set the
 *  AERIAL_FOG define that unlocks the aerial path in the global chunks. Skips fog:false materials
 *  (dome/wind/perimeter) and anything already patched. Materials created AFTER a patch sweep (e.g.
 *  the army's per-wave meshes) compile the safe classic path until the next sweep picks them up. */
export function patchFogMaterial(mat: THREE.Material): void {
  const m = mat as unknown as Patchable;
  if (m.fog === false) return; // opted out — leave unfogged
  if (m.userData && m.userData._aerialFog) return;
  m.userData = m.userData || {};
  m.userData._aerialFog = true;
  m.defines = m.defines || {};
  m.defines.AERIAL_FOG = "";
  const prev = mat.onBeforeCompile;
  mat.onBeforeCompile = function (shader, renderer) {
    if (prev) prev.call(this, shader, renderer);
    Object.assign(shader.uniforms, fogUniforms);
  };
  m.needsUpdate = true;
}

/** Patch every fogged material under an object tree. Idempotent + cheap; call after (re)building the scene. */
export function patchSceneFog(root: THREE.Object3D): void {
  root.traverse((o) => {
    const mesh = o as THREE.Mesh;
    const mat = mesh.material;
    if (!mat) return;
    if (Array.isArray(mat)) mat.forEach(patchFogMaterial);
    else patchFogMaterial(mat);
  });
}

// ---- per-biome fog, in HUMAN units (the dev sliders + Roblox-style knobs read these) --------------------
export interface FogBiome {
  density: number;      // 0..~5 optical master → per-stud density = density × FOG_DENSITY_UNIT (Roblox Density)
  airThickness: number; // studs; altitude e-folding scale → height falloff = 1 / airThickness (Roblox Haze)
  glare: number;        // 0..~2 sun forward-glow strength (Roblox Glare)
  glareG: number;       // 0..0.9 HG asymmetry (glow tightness)
  max: number;          // 0.3..1 opacity cap (Roblox Offset: 1 = dissolve, <1 = keep silhouettes)
  heightRef: number;    // studs; the "ground" altitude where density is full
}

export const FOG_DENSITY_UNIT = 1e-4;

// Bases tuned so each world dissolves ~92% by its old fogFar; thin clean worlds reach further, dusty/humid
// worlds hug the ground. All dev-tunable via the "Atmosphere — Fog" bank.
export const FOG_BIOMES: Record<string, FogBiome> = {
  hot: { density: 2.3, airThickness: 1400, glare: 0.5, glareG: 0.78, max: 1, heightRef: 0 },       // fierce sun
  cold: { density: 1.7, airThickness: 1800, glare: 0.14, glareG: 0.6, max: 1, heightRef: 0 },      // thin, clean, far
  temperate: { density: 2.0, airThickness: 1300, glare: 0.26, glareG: 0.76, max: 1, heightRef: 0 },// dusty rust
  garden: { density: 2.7, airThickness: 1100, glare: 0.2, glareG: 0.7, max: 1, heightRef: 0 },     // humid, hugs ground
};

let _current: FogBiome = { ...FOG_BIOMES.temperate };

/** Apply a biome's fog params (human units → uniforms). Resets the storm multiplier. */
export function setFogBiome(b: FogBiome): void {
  _current = { ...b };
  fogUniforms.uFogDensity.value = b.density * FOG_DENSITY_UNIT;
  fogUniforms.uFogHeightFalloff.value = 1 / Math.max(50, b.airThickness);
  fogUniforms.uFogGlare.value = b.glare;
  fogUniforms.uFogGlareG.value = b.glareG;
  fogUniforms.uFogMax.value = b.max;
  fogUniforms.uFogHeightRef.value = b.heightRef;
  fogUniforms.uFogStorm.value = 1;
}

/** The current biome's fog params (for seeding the dev sliders so their defaults match the world). */
export function getFogParams(): FogBiome {
  return { ..._current };
}

/** Point the fog at the baked sky cubemap + sun, and switch on aerial mode. Call from Atmosphere.install(). */
export function setFogSky(sky: THREE.Texture, sunDir: THREE.Vector3, sunColor: THREE.Color): void {
  fogUniforms.uFogSky.value = sky;
  fogUniforms.uFogSunDir.value.copy(sunDir).normalize();
  fogUniforms.uFogSunColor.value.copy(sunColor).convertLinearToSRGB(); // into the fog's display space
  fogUniforms.uFogAerial.value = 1;
}

/** Transient density multiplier (1 = calm). Drives the temperate dust-storm haze pulse. */
export function setFogStorm(mult: number): void {
  fogUniforms.uFogStorm.value = mult;
}
